import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import TeamOpenToInvitationSchema, {
  TeamOpenToInvitationJSON,
  TeamOpenToInvitationParams,
} from "marketplace/teams/invitations/_schemas/team-open-to-invitation/team-open-to-invitation-schema";

/**
 * A team the signed-in viewer may put somebody onto, as the Invite to Team picker offers it.
 *
 * The server decides both parts of that sentence — which teams appear at all, and whether the
 * person being looked at is already on each. Nothing here re-derives either, because a picker that
 * worked out eligibility from a broader list would be offering what the API has not agreed to.
 */
export default class TeamOpenToInvitation {
  public static readonly EXAMPLE: TeamOpenToInvitation = TeamOpenToInvitation.create({
    id: Guid.generate(),
    name: "The A Team",
    avatarId: Guid.generate(),
    inviteeAlreadyBelongs: false,
  });

  public static create(params: TeamOpenToInvitationParams): TeamOpenToInvitation {
    const parsed = TeamOpenToInvitationSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid TeamOpenToInvitation parameters.");
    }
    return new TeamOpenToInvitation(parsed);
  }

  public static fromJSON(json: TeamOpenToInvitationJSON): TeamOpenToInvitation {
    const params = TeamOpenToInvitationSchema.deserialize(json);
    if (isNullOrUndefined(params)) {
      throw new Error("Invalid TeamOpenToInvitation JSON.");
    }
    return new TeamOpenToInvitation(params);
  }

  public static fromJSONArray(jsonArray: TeamOpenToInvitationJSON[]): TeamOpenToInvitation[] {
    return jsonArray.map((json) => TeamOpenToInvitation.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly name: string;
  public readonly avatarId?: Guid;
  public readonly inviteeAlreadyBelongs: boolean;

  public constructor(params: TeamOpenToInvitationParams) {
    this.id = params.id;
    this.name = params.name;
    this.avatarId = params.avatarId;
    this.inviteeAlreadyBelongs = params.inviteeAlreadyBelongs;
  }

  public toJSON(): TeamOpenToInvitationJSON {
    return TeamOpenToInvitationSchema.serialize(this);
  }

  public isEqualTo(other: TeamOpenToInvitation): boolean {
    const sameAvatar =
      (isNullOrUndefined(this.avatarId) && isNullOrUndefined(other.avatarId)) ||
      (!isNullOrUndefined(this.avatarId) &&
        !isNullOrUndefined(other.avatarId) &&
        this.avatarId.isEqualTo(other.avatarId));
    return (
      this.id.isEqualTo(other.id) &&
      this.name === other.name &&
      sameAvatar &&
      this.inviteeAlreadyBelongs === other.inviteeAlreadyBelongs
    );
  }

  public toString(): string {
    return `TeamOpenToInvitation { id: ${this.id.toString()}, name: ${this.name}, inviteeAlreadyBelongs: ${this.inviteeAlreadyBelongs} }`;
  }
}
